import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

export interface ReorderColumnsRequest {
  columns: Array<{
    columnId: string;
    position: number;
  }>;
}

export interface ReorderColumnsResponse {
  message: string;
  columns: Array<{
    id: string;
    name: string;
    description: string;
    color: string;
    position: number;
  }>;
}

async function reorderColumns(data: ReorderColumnsRequest, token: string | undefined, workspaceId: string) {
  const bearer = resolveBearerToken(token);
  const headers = backendRequestHeaders(bearer || undefined);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token. Please sign in again.');
  }

  const body = data.columns.map((c) => ({ id: c.columnId, position: c.position }));
  const response = await axios.put<ReorderColumnsResponse>(
    `${getBackendBaseUrl()}/workspaces/${workspaceId}/tickets/columns/reorder`,
    body,
    { headers },
  );

  return response.data;
}

export function useReorderColumns() {
  const { user } = useAuth();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const queryClient = useQueryClient();

  // Helper function to extract workspace ID from workspace object
  const extractWorkspaceId = (workspaceObj: any) => {
    if (!workspaceObj) return '';
    if (workspaceObj.id) return workspaceObj.id;
    if (workspaceObj.SK) {
      if (workspaceObj.SK.startsWith('WS#')) {
        return workspaceObj.SK.substring(3);
      }
      return workspaceObj.SK;
    }
    return '';
  };

  const workspaceId = extractWorkspaceId(workspace);

  return useMutation({
    mutationFn: (data: ReorderColumnsRequest) => {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }

      return reorderColumns(data, user?.signInUserSession?.idToken?.jwtToken, workspaceId);
    },
    onMutate: async (data) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries([queryKeys.tickets, workspaceId]);

      // Snapshot the previous value
      const previousTickets = queryClient.getQueryData([queryKeys.tickets, workspaceId]);

      // Optimistically update column positions in cache
      queryClient.setQueryData([queryKeys.tickets, workspaceId], (old: any) => {
        if (!old) return old;

        console.log('🔮 Optimistic update: Reordering columns');

        const newColumns = old.columns.map((col: any) => {
          const update = data.columns.find((u) => u.columnId === col.id);
          if (update) {
            return { ...col, position: update.position };
          }
          return col;
        });

        // Sort by position
        newColumns.sort((a: any, b: any) => a.position - b.position);

        return { ...old, columns: newColumns };
      });

      return { previousTickets };
    },
    onSuccess: async () => {
      console.log('✅ Columns reordered successfully');
      console.log('🔄 Invalidating tickets query for workspace:', workspaceId);

      // Invalidate tickets query - React Query will refetch and sync with server
      await queryClient.invalidateQueries([queryKeys.tickets, workspaceId]);

      console.log('✅ Query invalidated, refetch will happen automatically');
    },
    onError: (err: any, variables, context: any) => {
      console.error('❌ Column reorder failed:', err);

      // Rollback to previous state on error
      if (context?.previousTickets) {
        queryClient.setQueryData([queryKeys.tickets, workspaceId], context.previousTickets);
        console.log('🔙 Rolled back to previous state due to error');
      }

      // Handle specific error cases
      if (err.response?.status === 400) {
        throw new Error('Invalid column data. Please check your input and try again.');
      } else if (err.response?.status === 401) {
        throw new Error('Unauthorized. Please log in again.');
      } else if (err.response?.status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      } else if (err.response?.status === 404) {
        throw new Error('Column not found.');
      } else if (err.response?.status === 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error(err.message || 'Failed to reorder columns. Please try again.');
      }
    },
  });
}
